// Sprint 2026-Mar-01 — Payment Integrity: Verify Beneficiaries dialog.
//
// Every "Download Excel" click on the Post Payment screen must route
// through this dialog. It calls
//     GET /api/batch-payments/{bid}/beneficiary-verification
// and renders one row per BPV / advance with its FROZEN beneficiary
// snapshot (masked account number). Full download requires every row
// `status === "ok"`; if some rows fail, an amber "Download (Skip N
// Unverified)" button exports only the verified rows (backend
// `?skip_unverified=true`) — unverified rows stay in the batch.
//
// The dialog owns the actual /export-excel fetch itself so operators
// cannot bypass verification by directly clicking Download.
//
// Design constraints:
//   • Account numbers are masked here (last-4 only). The full number
//     lives only in the server-generated bank Excel — never in JSON.
//   • Layout is a tight table so a 50-line batch fits in one modal
//     without excessive scrolling.

import { useEffect, useState, useCallback } from "react";
import { api, formatINR, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { X, Download, ShieldCheck, ShieldAlert, Loader2, Wrench } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLE = {
  ok:                  { color: "text-emerald-700 bg-emerald-50 border-emerald-200", label: "OK" },
  missing_snapshot:    { color: "text-rose-700 bg-rose-50 border-rose-200",         label: "MISSING SNAPSHOT" },
  missing_vendor_id:   { color: "text-rose-700 bg-rose-50 border-rose-200",         label: "MISSING VENDOR ID" },
  missing_vendor_code: { color: "text-rose-700 bg-rose-50 border-rose-200",         label: "MISSING VENDOR CODE" },
  missing_ifsc:        { color: "text-rose-700 bg-rose-50 border-rose-200",         label: "MISSING IFSC" },
  missing_account_number: { color: "text-rose-700 bg-rose-50 border-rose-200",      label: "MISSING ACCOUNT NUMBER" },
};

export default function BeneficiaryVerificationDialog({ batch, onClose }) {
  const { user } = useAuth();
  const [loading, setLoading]   = useState(true);
  const [data, setData]         = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [fixing, setFixing]     = useState(false);
  const [unresolved, setUnresolved] = useState(null); // rows the auto-fix could not resolve

  const isAdmin = user?.role === "admin";

  const load = useCallback(() => {
    if (!batch?.id) return;
    setLoading(true);
    setErrorMsg("");
    api.get(`/batch-payments/${batch.id}/beneficiary-verification`)
      .then(r => setData(r.data))
      .catch(e => setErrorMsg(formatApiErrorDetail(
        e?.response?.data?.detail ?? e?.response?.data ?? e?.message,
      ) || "Failed to load verification report"))
      .finally(() => setLoading(false));
  }, [batch?.id]);

  useEffect(() => { load(); }, [load]);

  async function doAutoFix() {
    setFixing(true);
    setUnresolved(null);
    try {
      const r = await api.post("/admin/backfill-beneficiary-snapshots",
                               { batch_number: batch?.batch_number });
      const fixed = r.data?.fixed || 0;
      const bad   = r.data?.unresolved || [];
      if (fixed > 0) toast.success(`${fixed} row(s) fixed — beneficiary snapshots frozen`);
      if (bad.length > 0) {
        toast.warning(`${bad.length} row(s) still need attention — see reasons below`);
        setUnresolved(bad);
      } else if (fixed === 0) {
        toast.info("Nothing to fix — no rows could be auto-resolved");
      }
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(
        e?.response?.data?.detail ?? e?.message) || "Auto-fix failed");
    } finally {
      setFixing(false);
    }
  }

  async function doDownload(skipUnverified = false) {
    if (!skipUnverified && !data?.can_export) return;
    if (skipUnverified && !(data?.ok > 0)) return;
    setDownloading(true);
    try {
      const token = (document.cookie.split("token=")[1] || "").split(";")[0]
                    || localStorage.getItem("token") || "";
      const qs = skipUnverified ? "?skip_unverified=true" : "";
      const r = await fetch(`${BACKEND}/api/batch-payments/${batch.id}/export-excel${qs}`, {
        headers: { Authorization: `Bearer ${token}` }, credentials: "include",
      });
      if (!r.ok) {
        const t = await r.text().catch(() => "");
        toast.error(t || "Download failed");
        return;
      }
      const blob = await r.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href = url;
      const base = (batch.batch_number || "batch").replace(/\//g, "-");
      a.download = skipUnverified ? `${base}-partial.xlsx` : `${base}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      if (skipUnverified) {
        toast.success(`Bank Excel downloaded — ${data.issues} unverified row(s) skipped`);
      } else {
        toast.success("Bank Excel downloaded");
      }
      onClose?.();
    } catch (e) {
      toast.error("Download failed");
    } finally {
      setDownloading(false);
    }
  }

  const canExport = !!data?.can_export;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 overflow-y-auto py-10"
         data-testid="beneficiary-verification-dialog"
         role="dialog">
      <div className="bg-white shadow-2xl w-full max-w-5xl mx-4 border border-slate-300">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <div className="flex items-center gap-2">
            {canExport
              ? <ShieldCheck size={18} className="text-emerald-600"/>
              : <ShieldAlert size={18} className="text-amber-600"/>}
            <div>
              <div className="text-sm font-bold text-slate-800">Verify Beneficiaries</div>
              <div className="text-[11px] text-slate-500">
                Batch {batch?.batch_number} — {data ? `${data.ok}/${data.total} rows OK` : "…"}
              </div>
            </div>
          </div>
          <button data-testid="verify-dialog-close" onClick={onClose}
                  className="text-slate-500 hover:text-slate-800">
            <X size={16}/>
          </button>
        </div>

        <div className="p-4">
          {loading && (
            <div className="text-center py-8 text-slate-500 text-sm flex items-center justify-center gap-2">
              <Loader2 size={14} className="animate-spin"/> Loading verification report…
            </div>
          )}
          {!loading && errorMsg && (
            <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 p-3 rounded">
              {errorMsg}
            </div>
          )}
          {!loading && data && (
            <>
              {!canExport && (
                <div className="mb-3 flex items-start gap-2 text-xs bg-amber-50 border border-amber-300 text-amber-900 p-3">
                  <ShieldAlert size={14} className="mt-0.5 shrink-0"/>
                  <div className="flex-1">
                    <b>{data.issues} row(s) failed verification.</b> They don&apos;t
                    have a valid frozen beneficiary snapshot.
                    {isAdmin
                      ? <> Click <b>Auto-Fix Rows</b> to rebuild the snapshots from the
                          original voucher lines (strictly by Vendor ID / Code — never by name).</>
                      : <> Ask an administrator to open this dialog and run <b>Auto-Fix Rows</b>.</>}
                    {data.ok > 0 && (
                      <> Alternatively, you can download the Excel with only the{" "}
                        <b>{data.ok} verified</b> row(s) — the unverified row(s) will be
                        skipped and stay in the batch for a later download once fixed.</>
                    )}
                  </div>
                  {isAdmin && (
                    <button onClick={doAutoFix}
                            disabled={fixing}
                            data-testid="verify-dialog-autofix"
                            className="shrink-0 inline-flex items-center gap-1.5 text-xs px-3 py-1.5 border
                                       bg-amber-600 text-white border-amber-700 hover:bg-amber-700
                                       disabled:opacity-60 disabled:cursor-not-allowed">
                      {fixing ? <Loader2 size={12} className="animate-spin"/> : <Wrench size={12}/>}
                      {fixing ? "Fixing…" : "Auto-Fix Rows"}
                    </button>
                  )}
                </div>
              )}
              {unresolved && unresolved.length > 0 && (
                <div className="mb-3 text-xs bg-rose-50 border border-rose-200 text-rose-900 p-3"
                     data-testid="verify-unresolved-panel">
                  <div className="font-bold mb-1">
                    {unresolved.length} row(s) could not be auto-fixed — fix the Vendor Master, then run Auto-Fix again:
                  </div>
                  <ul className="list-disc ml-4 space-y-0.5 max-h-40 overflow-auto">
                    {unresolved.map((u, i) => (
                      <li key={i}>
                        <span className="font-mono">{u.voucher_number}</span>
                        {" — "}{u.reason}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="overflow-auto max-h-[60vh]">
                <table className="text-xs w-full border border-slate-200">
                  <thead className="bg-slate-100 text-slate-700 sticky top-0">
                    <tr>
                      <th className="text-left py-1.5 px-2 whitespace-nowrap">Voucher #</th>
                      <th className="text-left py-1.5 px-2 whitespace-nowrap">Vendor Code</th>
                      <th className="text-left py-1.5 px-2 whitespace-nowrap">Vendor Name</th>
                      <th className="text-left py-1.5 px-2 whitespace-nowrap">Account Holder</th>
                      <th className="text-left py-1.5 px-2 whitespace-nowrap">A/C (masked)</th>
                      <th className="text-left py-1.5 px-2 whitespace-nowrap">IFSC</th>
                      <th className="text-left py-1.5 px-2 whitespace-nowrap">Bank</th>
                      <th className="text-right py-1.5 px-2 whitespace-nowrap">Amount</th>
                      <th className="text-left py-1.5 px-2 whitespace-nowrap">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.rows || []).map((r, i) => {
                      const st = STATUS_STYLE[r.status] || STATUS_STYLE.missing_snapshot;
                      return (
                        <tr key={i}
                            data-testid={`verify-row-${i}`}
                            className={r.status === "ok" ? "" : "bg-rose-50/30"}>
                          <td className="py-1 px-2 font-mono text-[11px]">{r.voucher_number}</td>
                          <td className="py-1 px-2">{r.vendor_code || <span className="text-rose-600">—</span>}</td>
                          <td className="py-1 px-2">{r.vendor_name}</td>
                          <td className="py-1 px-2">{r.account_holder || "—"}</td>
                          <td className="py-1 px-2 font-mono text-[11px]">{r.account_number_masked || "—"}</td>
                          <td className="py-1 px-2 font-mono text-[11px]">{r.ifsc || "—"}</td>
                          <td className="py-1 px-2">{r.bank_name || "—"}</td>
                          <td className="py-1 px-2 text-right">{formatINR(r.amount || 0)}</td>
                          <td className="py-1 px-2">
                            <span className={`inline-block px-1.5 py-0.5 border text-[10px] ${st.color}`}
                                  data-testid={`verify-status-${i}`}>
                              {st.label}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        <div className="border-t border-slate-200 px-4 py-3 flex items-center justify-between gap-3">
          <div className="text-[11px] text-slate-500">
            {data && (
              <>
                <b className="text-slate-800">{data.ok}</b> beneficiary(ies) verified
                &nbsp;/&nbsp; <b className="text-slate-800">{data.total}</b> total
                {data.issues > 0 && (
                  <> &nbsp;/&nbsp; <b className="text-rose-700">{data.issues}</b> issue(s)</>
                )}
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose}
                    data-testid="verify-dialog-cancel"
                    className="text-xs px-3 py-1.5 border border-slate-300 text-slate-700 hover:bg-slate-100">
              Close
            </button>
            {!canExport && data?.ok > 0 && (
              <button onClick={() => doDownload(true)}
                      disabled={downloading}
                      data-testid="verify-dialog-download-skip"
                      className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 border
                                 bg-amber-600 text-white border-amber-700 hover:bg-amber-700
                                 disabled:opacity-60 disabled:cursor-not-allowed">
                {downloading ? <Loader2 size={12} className="animate-spin"/> : <Download size={12}/>}
                {downloading ? "Downloading…" : `Download (Skip ${data.issues} Unverified)`}
              </button>
            )}
            <button onClick={() => doDownload(false)}
                    disabled={!canExport || downloading}
                    data-testid="verify-dialog-download"
                    className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 border
                      ${canExport
                        ? "bg-emerald-600 text-white border-emerald-700 hover:bg-emerald-700"
                        : "bg-slate-200 text-slate-500 border-slate-300 cursor-not-allowed"}`}>
              {downloading ? <Loader2 size={12} className="animate-spin"/> : <Download size={12}/>}
              {downloading ? "Downloading…" : "Download Bank Excel"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
